if (!customElements.get('anchored-collections')) {
  class AnchoredCollections extends HTMLElement {
    constructor() {
      super();

      this.activeClass = 'is-active';
      this.visible = new Set();
      this.onClick = this.onClick.bind(this);
      this.onResize = this.onResize.bind(this);
    }

    connectedCallback() {
      this.slider = this.querySelector('.anchored-collections__slider');
      this.strip = this.querySelector('.anchored-collections__anchors');
      this.outer = this.querySelector('.anchored-collections__anchors-outer');

      this.initSlider();

      this.pairs = Array.from(

        this.querySelectorAll('.anchored-collections__anchor')
      )
        .map((link) => {
          // An anchor override can point at any element on the page, including
          // one this section did not render — or one that does not exist.
          const id = (link.getAttribute('href') || '').slice(1);
          const target = id ? document.getElementById(id) : null;

          return target ? { link, target } : null;
        })
        .filter(Boolean);

      // The jump itself is a plain anchor link and needs no JS; everything below
      // is the active-state highlight, so bail out rather than half-wire it.
      if (this.pairs.length < 2 || !('IntersectionObserver' in window)) return;

      this.addEventListener('click', this.onClick);
      window.addEventListener('resize', this.onResize);

      this.observe();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      window.removeEventListener('resize', this.onResize);

      if (this.swiper) this.swiper.destroy(true, true);
      if (this.observer) this.observer.disconnect();
      if (this.resizeTimer) clearTimeout(this.resizeTimer);
    }

        initSlider() {
      if (!this.slider || typeof Swiper === 'undefined') return;

      // Elements rather than selector strings: the theme's button classes are
      // not unique, and several sliders can share a page.
      const prevEl = this.slider.querySelector('.swiper-button--prev');
      const nextEl = this.slider.querySelector('.swiper-button--next');

      this.swiper = new Swiper(this.slider, {
        a11y: false,
        threshold: 3,
        slidesPerView: 'auto',
        spaceBetween: 12,
        resistanceRatio: 0.72,
        navigation: { prevEl, nextEl },
        breakpoints: {
          750: { spaceBetween: 20 }
        }
      });

      if (prevEl) prevEl.setAttribute('aria-label', 'Previous teams');
      if (nextEl) nextEl.setAttribute('aria-label', 'Next teams');
    }

    observe() {
      if (this.observer) this.observer.disconnect();
      this.visible.clear();

      // The header and the strip both sit over the page, so a group only counts
      // as current once its top has cleared them. The bottom margin keeps one
      // group active while most of it is on screen instead of handing over the
      // moment the next heading pokes into view.
      const headerHeight =
        parseInt(
          getComputedStyle(document.documentElement).getPropertyValue(
            '--header-height'
          ),
          10
        ) || 0;
      const stripHeight = this.outer ? this.outer.offsetHeight : 0;
      const offset = headerHeight + stripHeight;

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.visible.add(entry.target);
            } else {
              this.visible.delete(entry.target);
            }
          });

          // A click scrolls past every group in between; honour the clicked
          // bubble until the scroll settles rather than flickering through them.
          if (this.lockUntil && Date.now() < this.lockUntil) return;

          const current = this.pairs.find((pair) =>
            this.visible.has(pair.target)
          );
          if (current) this.setActive(current.link);
        },
        { rootMargin: `-${offset}px 0px -55% 0px`, threshold: 0 }
      );

      this.pairs.forEach((pair) => this.observer.observe(pair.target));
    }

    onResize() {
      if (this.resizeTimer) clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => this.observe(), 200);
    }

    onClick(event) {
      const link = event.target.closest('.anchored-collections__anchor');
      if (!link) return;

      this.setActive(link);
      this.lockUntil = Date.now() + 800;
    }

    setActive(link) {
      if (link === this.active) return;

      if (this.active) {
        this.active.classList.remove(this.activeClass);
        this.active.removeAttribute('aria-current');
      }

      this.active = link;
      link.classList.add(this.activeClass);
      link.setAttribute('aria-current', 'true');

      this.scrollIntoStrip(link);
    }

    scrollIntoStrip(link) {
      const slide = link.closest('.anchored-collections__anchors-item');
      const viewport = this.swiper ? this.slider : this.strip;
      if (!slide || !viewport) return;

      const viewportRect = viewport.getBoundingClientRect();
      const slideRect = slide.getBoundingClientRect();
      const isVisible =
        slideRect.left >= viewportRect.left &&
        slideRect.right <= viewportRect.right;

      if (isVisible) return;

      if (this.swiper) {
        // Swiper moves the row by transform, so scrollLeft means nothing here.
        const index = Array.from(this.swiper.slides).indexOf(slide);
        if (index > -1) this.swiper.slideTo(index);
        return;
      }

      viewport.scrollTo({
        left:
          viewport.scrollLeft +
          slideRect.left -
          viewportRect.left -
          (viewportRect.width - slideRect.width) / 2,
        behavior: 'smooth'
      });
    }
  }

  customElements.define('anchored-collections', AnchoredCollections);
}